/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { MenuPermissionsService } from './menu-permissions.service';

/**
 * Hiding a menu is not enough — someone with a bookmark could still open the
 * route directly. The guard asks the same question the sidenav rule asks, and
 * a "no" answer sends the person to the first menu they may see (the
 * dashboard when no menu is open to them) rather than letting the page load.
 *
 * It sits on the layout route as `canActivateChild`, so it covers the menus
 * the app declares and the ones the aca-content library declares. The whole
 * route path is handed over, so `workflows/start` is held to its own menu;
 * `repository/:folderId` still answers the way `repository` does.
 */
export const menuAllowedGuard: CanActivateChildFn = (route) => {
  const menuPerms = inject(MenuPermissionsService);
  const router = inject(Router);

  menuPerms.load();
  return menuPerms.whenLoaded.pipe(
    map(() => {
      const path = route.routeConfig?.path ?? '';
      return menuPerms.isRouteAllowed(path) ? true : router.parseUrl(menuPerms.firstAllowedRoute());
    })
  );
};
